import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  HeartPulse, RefreshCw, ChevronDown, ChevronUp, ArrowRight,
  CheckCircle2, AlertTriangle, XCircle,
} from "lucide-react";
import { Card, Spinner, PageHeader, Button } from "../lib/ui";
import { useToast } from "../lib/toast";
import api from "../lib/api";

const OVERALL = {
  healthy: { emoji: "🟢", label: "Sehat", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  attention: { emoji: "🟡", label: "Perlu Perhatian", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  critical: { emoji: "🔴", label: "Kritis", cls: "border-red-300 bg-red-50 text-red-700" },
};

const DOT = {
  ok: "bg-emerald-500",
  warning: "bg-amber-500",
  critical: "bg-red-500",
};

function StatusIcon({ status }) {
  if (status === "ok") return <CheckCircle2 size={16} className="text-emerald-600" />;
  if (status === "warning") return <AlertTriangle size={16} className="text-amber-600" />;
  return <XCircle size={16} className="text-red-600" />;
}

function HealthItem({ c }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isIssue = c.status !== "ok";
  return (
    <div className={`rounded-xl border transition-colors ${isIssue ? (c.status === "critical" ? "border-red-200" : "border-amber-200") : "border-line"}`} data-testid={`erp-health-item-${c.key}`}>
      <button className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left" onClick={() => setOpen(!open)} data-testid={`erp-health-toggle-${c.key}`}>
        <span className="flex items-center gap-3">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${DOT[c.status]}`} />
          <span>
            <span className="block text-sm font-bold text-ink">{c.label}</span>
            <span className="block text-xs text-ink-soft" data-testid={`erp-health-detail-${c.key}`}>{c.detail}</span>
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <StatusIcon status={c.status} />
          {open ? <ChevronUp size={14} className="text-ink-mute" /> : <ChevronDown size={14} className="text-ink-mute" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-line px-4 py-3 text-sm" data-testid={`erp-health-drill-${c.key}`}>
          <p className="mb-1"><b className="text-ink">Apa yang terjadi:</b> <span className="text-ink-soft">{c.detail}</span></p>
          <p className="mb-1"><b className="text-ink">Mengapa penting:</b> <span className="text-ink-soft">{c.why}</span></p>
          <p className="mb-2"><b className="text-ink">Cara memperbaiki:</b> <span className="text-ink-soft">{c.fix}</span></p>
          <Button size="sm" variant="outline" onClick={() => navigate(c.link)} data-testid={`erp-health-link-${c.key}`}>
            Buka halaman terkait <ArrowRight size={13} />
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ERPHealth() {
  const toast = useToast();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/integrity/health"); setD(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat status kesehatan"); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  if (loading && !d) return <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div>;
  if (!d) return null;

  const ov = OVERALL[d.overall] || OVERALL.attention;
  const issues = d.checks.filter((c) => c.status !== "ok").length;

  return (
    <div data-testid="erp-health-page">
      <PageHeader title="Kesehatan ERP" subtitle="Pemeriksaan harian 10 detik — status operasional Central Flow" icon={HeartPulse}>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="erp-health-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Periksa Ulang
        </Button>
      </PageHeader>

      {/* Overall hero */}
      <Card className={`mb-5 border-2 ${ov.cls.split(" ").slice(0, 2).join(" ")}`} data-testid="erp-health-hero">
        <div className="flex flex-col items-center py-2 text-center">
          <p className="text-5xl">{ov.emoji}</p>
          <p className={`mt-2 font-heading text-2xl font-bold ${ov.cls.split(" ")[2]}`} data-testid="erp-health-score">
            {ov.label} ({d.score}%)
          </p>
          <p className="mt-1 text-sm text-ink-soft" data-testid="erp-health-summary">
            {issues === 0 ? "Semua pemeriksaan kritis-produksi LOLOS. Selamat beroperasi! 🎉"
              : `${issues} hal butuh perhatian — klik item di bawah untuk penjelasan & cara memperbaiki.`}
          </p>
          <p className="mt-1 text-[11px] text-ink-mute">Diperiksa: {d.generated_at_wib}</p>
        </div>
      </Card>

      {/* Checks — issues first */}
      <div className="space-y-2">
        {[...d.checks].sort((a, b) => {
          const rank = { critical: 0, warning: 1, ok: 2 };
          return rank[a.status] - rank[b.status];
        }).map((c) => <HealthItem key={c.key} c={c} />)}
      </div>
    </div>
  );
}
